import { z } from 'zod';

/**
 * Catálogo de PERMISOS de la plataforma (RBAC por permisos).
 *
 * Cada permiso es `<recurso>:<acción>`. La autorización del backend valida por
 * permiso (no por rol), de modo que se pueden crear roles/perfiles a medida
 * marcando exactamente qué puede hacer cada uno. `*` = todos los permisos.
 *
 * IMPORTANTE: al añadir un endpoint nuevo, define su permiso aquí y protégelo
 * con `@RequirePermission(...)`. Al añadir un permiso, agrégalo también a los
 * roles built-in que deban tenerlo (`BUILTIN_ROLE_PERMISSIONS`).
 */
export const PERMISSION_KEYS = [
  // Pacientes
  'patients:read',
  'patients:write',
  'patients:portal-credentials',
  // Procedimientos / episodios
  'procedures:read',
  'procedures:write',
  'procedures:transition',
  'procedures:patient-status',
  'procedures:cancel',
  'procedures:delete',
  // Consentimiento informado
  'consent:read',
  'consent:sign',
  // Registro de anestesia
  'anesthesia:read',
  'anesthesia:sign',
  // Hoja de gasto
  'expense:read',
  'expense:sign',
  // Resultados (adjuntos del procedimiento)
  'results:read',
  'results:write',
  'results:delete',
  // Patología (entrega de resultados)
  'pathology:read',
  'pathology:sign',
  // Nómina de enfermería
  'payroll:read',
  'payroll:clock',
  'payroll:manage',
  // Catálogos (insumos / medicamentos)
  'catalog:read',
  'catalog:manage',
  // Cuerpos de consentimiento (plantillas)
  'consent-bodies:manage',
  // Documentos clínicos (listados)
  'clinical-docs:read',
  // Tableros
  'dashboard:read',
  'reports:read',
  // Auditoría
  'audit:read',
  // Usuarios y roles
  'users:read',
  'users:read-firma',
  'users:manage',
  'roles:manage',
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

/** Comodín de "todos los permisos" (roles administrativos). */
export const ALL_PERMISSIONS = '*' as const;

/** Un permiso almacenado/asignado: una clave del catálogo o el comodín `*`. */
export const PermissionSchema = z.union([
  z.literal(ALL_PERMISSIONS),
  z.enum(PERMISSION_KEYS),
]);
export type Permission = z.infer<typeof PermissionSchema>;

/**
 * Metadatos de cada permiso para el CONSTRUCTOR de perfiles en la UI: etiqueta
 * legible y grupo (recurso) para agruparlos en la pantalla.
 */
export interface PermissionMeta {
  readonly key: PermissionKey;
  readonly grupo: string;
  readonly label: string;
}

export const PERMISSION_CATALOG: readonly PermissionMeta[] = [
  { key: 'patients:read', grupo: 'Pacientes', label: 'Ver pacientes' },
  { key: 'patients:write', grupo: 'Pacientes', label: 'Crear y editar pacientes' },
  { key: 'patients:portal-credentials', grupo: 'Pacientes', label: 'Ver credenciales del portal' },
  { key: 'procedures:read', grupo: 'Procedimientos', label: 'Ver procedimientos' },
  { key: 'procedures:write', grupo: 'Procedimientos', label: 'Crear y editar procedimientos' },
  { key: 'procedures:transition', grupo: 'Procedimientos', label: 'Cambiar etapa del procedimiento' },
  { key: 'procedures:patient-status', grupo: 'Procedimientos', label: 'Cambiar estado del paciente' },
  { key: 'procedures:cancel', grupo: 'Procedimientos', label: 'Cancelar procedimientos' },
  { key: 'procedures:delete', grupo: 'Procedimientos', label: 'Eliminar procedimientos (borrado definitivo)' },
  { key: 'consent:read', grupo: 'Consentimiento', label: 'Ver consentimientos' },
  { key: 'consent:sign', grupo: 'Consentimiento', label: 'Firmar consentimientos' },
  { key: 'anesthesia:read', grupo: 'Anestesia', label: 'Ver registros de anestesia' },
  { key: 'anesthesia:sign', grupo: 'Anestesia', label: 'Firmar registros de anestesia' },
  { key: 'expense:read', grupo: 'Hoja de gasto', label: 'Ver hojas de gasto' },
  { key: 'expense:sign', grupo: 'Hoja de gasto', label: 'Firmar hojas de gasto' },
  { key: 'results:read', grupo: 'Resultados', label: 'Ver y descargar resultados' },
  { key: 'results:write', grupo: 'Resultados', label: 'Subir resultados' },
  { key: 'results:delete', grupo: 'Resultados', label: 'Eliminar resultados' },
  { key: 'pathology:read', grupo: 'Patología', label: 'Ver entregas de patología' },
  { key: 'pathology:sign', grupo: 'Patología', label: 'Firmar entrega de patología' },
  { key: 'payroll:read', grupo: 'Nómina', label: 'Ver nómina y turnos' },
  { key: 'payroll:clock', grupo: 'Nómina', label: 'Registrar entrada/salida de turno' },
  { key: 'payroll:manage', grupo: 'Nómina', label: 'Gestionar tarifas y nómina total' },
  { key: 'catalog:read', grupo: 'Catálogos', label: 'Ver insumos y medicamentos' },
  { key: 'catalog:manage', grupo: 'Catálogos', label: 'Gestionar insumos y medicamentos' },
  { key: 'consent-bodies:manage', grupo: 'Consentimientos', label: 'Editar plantillas de consentimiento' },
  { key: 'clinical-docs:read', grupo: 'Documentos', label: 'Ver documentos clínicos' },
  { key: 'dashboard:read', grupo: 'Tableros', label: 'Ver el dashboard' },
  { key: 'reports:read', grupo: 'Tableros', label: 'Ver reportes' },
  { key: 'audit:read', grupo: 'Auditoría', label: 'Ver la bitácora de auditoría' },
  { key: 'users:read', grupo: 'Usuarios y roles', label: 'Ver usuarios' },
  { key: 'users:read-firma', grupo: 'Usuarios y roles', label: 'Ver la firma de un usuario' },
  { key: 'users:manage', grupo: 'Usuarios y roles', label: 'Crear, editar y resetear contraseñas' },
  { key: 'roles:manage', grupo: 'Usuarios y roles', label: 'Crear y editar perfiles (roles)' },
];

/**
 * ¿El conjunto de permisos `held` satisface el permiso `required`?
 * `*` en `held` concede todo.
 */
export function hasPermission(
  held: readonly string[],
  required: Permission,
): boolean {
  return held.includes(ALL_PERMISSIONS) || held.includes(required);
}
